/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { I18nService } from '@dm/core';
import { type SelectOption } from '@dm/core/types';
import { SecurityModeE } from 'app/feature/service/file-service/file-system/file-system-str.service';

/**
 * NAS协议安全模式
 */
export enum SecurityStyle {
    Mixed = '0',
    Native = '1',
    NTFS = '2',
    UNIX = '3'
}

/**
 * NAS锁定策略
 * 0：Mandatory策略
 * 1：Advisory策略
 */
export enum NasLockingPolicy {
    Mandatory = '0',
    Advisory = '1',
}

@Injectable({ providedIn: 'root' })
export class DtreeStrService {
    i18n = {
        btn: {
            createBtn: this.get('common.create_button'),
            deleteBtn: this.get('common.delete_button'),
            modifyBtn: this.get('common.modify_button'),
            okBtn: this.get('common.confirm_button'),
            cancelBtn: this.get('common.cancel_button'),
            closeBtn: this.get('common.close_button'),
            actionBtn: this.get('common.operation_button'),
            addBtn: this.get('common.add_button'),
            removeBtn: this.get('common.remove_button'),
            createNFSBtn: this.get('service.share_term_createNFS_title'),
            createCIFSBtn: this.get('service.share_term_createCIFS_title'),
            createDataTurboBtn: this.get('service.share_term_createDataTurbo_title'),
            createQuotaBtn: this.get('service.quota_term_create_createQuota_tittle'),
            createDataTurboDisabledTip: this.get('service.share_term_createDataTurbo_err_tip'),
        },
        label: {
            //  名称
            name: this.get('common.name_column'),
            // 描述
            description: this.get('common.description_column'),
            // 文件系统
            fileSystem: this.get('common.fileSystem_title'),
            // 文件系统名
            fileSystemName: this.get('service.quota_term_FSName_lab'),
            // 所属文件系统
            ownFileSystem: this.get('service.dtree_task_create_owningFS_lab'),
            // 所属租户
            vStoreLab: this.get('common.owningVstore_lab'),
            // dtree
            dtree: this.get('common.dtree_lab'),
            // 共享
            share: this.get('common.share_lab'),
            // 共享[单数]
            shareSingleLab: this.get('common.EnumDefine_MOTYPE_SHARE'),
            // nfs
            nfsShare: this.get('common.nfsShare_lab'),
            // cifs
            cifsShare: this.get('common.cifsShare_lab'),
            // dataTurbo
            dataTurboShare: this.get('common.DataTurboShare_lab'),
            // nfs小写
            nfsShareLowercase: this.get('monitor.charts_term_objectType_NFSShare_icon_label'),
            // cifs小写
            cifsShareLowercase: this.get('monitor.charts_term_objectType_CIFSshare_icon_label'),
            // dataTurbo小写
            dataTurboShareLowercase: this.get('common.DataTurboShare_icon_lab'),
            // 配额
            quota: this.get('common.quota_lab'),
            // 自定义配额
            userQuota: this.get('service.quota_term_userQuotas_tittle'),
            // 配额报表
            quotaReport: this.get('service.quota_term_quotaReports_tittle'),
            quotas: this.get('common.quota_title'),
            // 已配置配额
            configedQuota: this.get('service.filesystem_term_quotaConfigured_lab'),
            // 所属存储池
            storagePool: this.get('storage.LunGroup_term_belongedStoragePool_label'),
            // ID
            id: this.get('common.ID_column'),
            // 类型
            type: this.get('common.type_column'),
            // 高级
            advance: this.get('common.advance_label'),
            // 已用容量
            usedCapacity: this.get('common.term_cap_usedCpa_lab'),
            // 安全模式
            security: this.get('service.dtree_task_create_securityStyle_lab'),
            // NAS锁定策略
            lockingPolicy: this.get('service.fileSystem_task_create_lockPolicy_lab'),
            // 目录
            path: this.get('service.dtree_task_create_directory_lab'),
            // 父目录路径
            fatherPath: this.get('service.dtree_task_create_fatherPath_lab'),
            // 全路径
            fullPath: this.get('service.dtree_task_create_fullPath_lab'),
            // 配额开关
            quotaSwitchLab: this.get('service.fileSystem_term_modify_quotaSwitch_lab'),
            // 配额状态
            quotaStatusLab: this.get('service.fileSystem_term_view_quotaStatus_col'),
            // 启用
            enable: this.get('common.enable_label'),
            // 高级
            advancedLab: this.get('common.advance_label'),
        },
        title: {
            // 基本信息
            basicInfo: this.get('common.basicInfo_title'),
            // 选择文件系统
            selectFS: this.get('service.share_term_create_chooseFilesystem_title'),
            // 选择Dtree
            selectDtree: this.get('service.share_term_create_chooseDtree_title'),
            // 概要信息
            summary: this.get('common.task_collectInfo_title_label'),
            // 删除Dtree
            deleteDtree: this.get('service.dtree_term_delete_title'),
            // 修改Dtree
            modifyDtree: this.get('service.dtree_term_modify_title'),
            // 创建Dtree
            createDtree: this.get('service.dtree_term_create_title'),
        },
        tip: {
            path: this.get('service.dtree_task_create_Path_tip'),
            security: this.get('service.dtree_task_create_securityStyle_all_tip'),
            dtree: this.get('service.dtree_term_create_name_validation', [1, 255]),
            createDataTurboDisabledTip: this.get('service.share_term_createDataTurbo_err_tip'),
            modifyNameMsg: this.get('common.dtree_task_modifyName_msg'),
        },
        error: {
            dtreeModify: this.get('service.dtree_term_modify_name_validation'),
        },
        highRisk: {
            deleteDtree: this.get('service.dtree_task_deleteDtree_confirm_msg'),
        },
        rest: {
            // 创建Dtree{0}
            createDtree: this.get('rest.createDtree_msg'),
            // 修改Dtree{0}
            modifyDtree: this.get('rest.modifyDtree_msg'),
            // 删除Dtree{0}
            deleteDtree: this.get('rest.deleteDtree_msg'),
        },
        value: {

        },
    };

    /**
     * 是否配置了配额
     */
    quotaOpts: SelectOption[] = [
        {
            label: this.i18nService.get('common.configured_lab'),
            value: true,
        },
        {
            label: this.i18nService.get('common.unconfigured_lab'),
            value: false,
        },
    ];

    /**
     * 共享类型
     *
     * CIFS NFS null
     */
    shareOpts: SelectOption[] = [
        {
            label: this.i18nService.get('common.NFS_lab'),
            value: 'NFS',
        },
        {
            label: this.i18nService.get('common.CIFS_lab'),
            value: 'CIFS',
        },
        {
            label: this.i18nService.get('common.unconfigured_lab'),
            value: 'null',
        },
    ];

    /**
     * @todo kkkkkkkkk
     * 临时用一下，当前版本只有UNIX
     *
     *  3：UNIX安全模式
     *
     */
    tempSecurityOpts: SelectOption[] = [
        {
            label: this.i18nService.get('service.dtree_task_create_securityStyle_UNIX_value'),
            value: SecurityModeE.UNIX,
        },
        {
            label: this.i18nService.get('service.dtree_task_create_securityStyle_NTFS_value'),
            value: SecurityModeE.NTFS,
        },
        {
            label: this.i18nService.get('service.dtree_task_create_securityStyle_Native_value'),
            value: SecurityModeE.Native,
        },
        {
            label: this.get('service.filesystem_term_securityStyle_mixed_lab'),
            value: SecurityModeE.Mixed,
        },
    ];

    /**
     * NAS锁定策略
     * 0：Mandatory策略
     * 1：Advisory策略
     */
    nasLockingPolicyOpts: SelectOption[] = [
        {
            label: this.get('service.fileSystem_task_create_lockPolicy_mandatory_value'),
            value: NasLockingPolicy.Mandatory,
        },
        {
            label: this.get('service.fileSystem_task_create_lockPolicy_advisory_value'),
            value: NasLockingPolicy.Advisory,
        },
    ];

    /**
     *  配额开关状态
     *  0：关闭
     *  1：开启
     *  2：正在初始化
     */
    quotaSwitchStatusOpts: SelectOption[] = [
        {
            label: this.i18nService.get('common.disabled_switch_column'),
            value: '0',
        },
        {
            label: this.i18nService.get('common.enabled_switch_column'),
            value: '1',
        },
        {
            label: this.i18nService.get('common.EnumDefine_RUNNING_STATUS_E_INITIALIZING'),
            value: '2',
        },
    ];

    /**
     * 安全模式
     *
     * 0：Mixed安全模式
     *  1：Native安全模式
     *  2：NTFS安全模式
     *  3：UNIX安全模式
     *
     */
    securityOpts: SelectOption[] = [
        {
            label: this.i18nService.get('service.dtree_task_create_securityStyle_mixed_value'),
            value: '0',
        },
        {
            label: this.i18nService.get('service.dtree_task_create_securityStyle_Native_value'),
            value: '1',
        },
        {
            label: this.i18nService.get('service.dtree_task_create_securityStyle_NTFS_value'),
            value: '2',
        },
        {
            label: this.i18nService.get('service.dtree_task_create_securityStyle_UNIX_value'),
            value: '3',
        },
    ];

    constructor(
        private i18nService: I18nService,
    ) {
    }

    get(key, params?, colon?): string {
        return this.i18nService.get(key, params, colon);
    }

    requiredErr(name: string): string {
        return this.get('common.null_validation', [name]);
    }

    rangeByteLengthErr(min: number, max: number): string {
        return this.get('common.length_msg', [min, max]);
    }

    rangeByteOneLengthErr(min: number, max: number): string {
        return this.get('common.length_oneValue_tip', [min, max]);
    }

    commonNameErr(name: string): string {
        return this.get('common.name_vadlitaion', [name]);
    }

    deleteResult(name: string): string {
        return this.get('rest.delete_result_tip', [name]);
    }

    repeatErr(name: string): string {
        return this.get('common.existence_validation', [name]);
    }
}
